import React, {useEffect, useRef, useState} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import throttle from 'lodash/throttle'
import {isNil} from 'ramda'
import {Spacing} from 'types/Theme'

import {useDeviceLayout} from '../../../../context/deviceLayout'
import {cssTheme} from '../../../../themes'
import {Button, ButtonSize} from '../../../designSystem/Button'
import {Icon} from '../../../designSystem/Icon'
import {Box} from '../Box'

const THROTTLING_INTERVAL = 100 // ms

interface Props {
  /** Elements that will be rendered inside the carousel */
  children: React.ReactNode
  /** Whether the language is RTL or not */
  isRtlLanguage?: boolean
  /** Custom prop to hide nav buttons only on mobileSm resolution (false by default) */
  hideNavButtonsOnlyForMobileSm?: boolean
  /** A function to run when scroll happens */
  onScroll?: () => void
  hasSidePaddings?: boolean
  itemsGap?: Spacing
  /** Navigation buttons size */
  navigationButtonSize?: ButtonSize
  navigationButtonLabels?: {prev: string; next: string}
  renderHeader?: (navigation: React.ReactNode) => React.ReactNode
  sidePadding?: Spacing
  /** Observe container resizing and keep arrow clicks moving at least one card. */
  responsiveNavigation?: boolean
}

const ScrollWrapper = styled.div<{
  itemsGap: Spacing
  sidePadding: Spacing
}>`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;

  /* hide scrollbar */
  -ms-overflow-style: none; /* for Edge */
  scrollbar-width: none; /* for Firefox */
  &::-webkit-scrollbar {
    display: none; /* for Chrome, Safari and Opera */
  }
  overflow-scrolling: touch;

  scroll-snap-type: x mandatory;
  scroll-padding: 0 ${cssTheme.layout.spacing.s200};

  > * + * {
    margin-left: ${({itemsGap}) => cssTheme.layout.spacing[itemsGap]};
  }
  &.--has-side-paddings {
    padding-left: ${({sidePadding}) => cssTheme.layout.spacing[sidePadding]};
    padding-right: ${({sidePadding}) => cssTheme.layout.spacing[sidePadding]};
  }
  /* The end margin of SmoothScrollNavigation is not taken into account.
    * See this thread: https://stackoverflow.com/questions/38993170 */
  &:after {
    content: '';
    flex: 0 0 1px;
  }
`

const CardsAndNavButtonsWrapper = styled.div`
  position: relative;
`

/* Fades out the partially visible cards at the edges. An overlay instead of a
 * mask-image on ScrollWrapper because Chromium skips ancestor masks on
 * composited images, leaving photos opaque while the rest fades. */
const EdgeFadeOverlay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  /* Above card image sections, which set their own z-index */
  z-index: 1;
  width: 15%;
  pointer-events: none;
  &.--direction-prev {
    left: 0;
    background: linear-gradient(
      to right,
      ${cssTheme.colors.background.neutral.primary},
      transparent
    );
  }
  &.--direction-next {
    right: 0;
    background: linear-gradient(
      to left,
      ${cssTheme.colors.background.neutral.primary},
      transparent
    );
  }
`

const NavigateButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 2;
  margin: ${cssTheme.layout.spacing.s200} 0;
  &.--direction-prev {
    left: 0;
  }
  &.--direction-next {
    right: 0;
  }
  [dir='rtl'] & {
    transform: scale(-1);
  }
`

export const SmoothScrollNavigation = ({
  children,
  isRtlLanguage = false,
  hideNavButtonsOnlyForMobileSm,
  onScroll,
  hasSidePaddings,
  itemsGap = 's500',
  navigationButtonSize = 'sm',
  navigationButtonLabels,
  renderHeader,
  sidePadding = 's500',
  responsiveNavigation = false
}: Props) => {
  const {isMobile, isMobileSm} = useDeviceLayout()
  const [cardsPerClick, setCardsPerClick] = useState(2)
  const [cardWidth, setCardWidth] = useState(0)
  const [viewportWidth, setViewportWidth] = useState(0)
  const [scrollPosition, setScrollPosition] = useState(0)

  const scrollWrapperRef = useRef<HTMLDivElement>(null)
  const [navButtonVisibility, setNavButtonVisibility] = useState({
    next: false,
    prev: false
  })

  useEffect(() => {
    const ref = scrollWrapperRef.current

    if (!ref) {
      return
    }

    const throttledSetScrollPosition = throttle(() => {
      setScrollPosition(Math.round(ref.scrollLeft))
    }, THROTTLING_INTERVAL)

    const handleScroll = () => {
      throttledSetScrollPosition()
      onScroll?.()
    }

    ref.addEventListener('scroll', handleScroll)
    return () => ref.removeEventListener('scroll', handleScroll)
  }, [])

  const container = scrollWrapperRef.current
  const {
    clientWidth,
    scrollWidth,
    scrollLeft: containerScrollLeft
  } = container || {}
  const scrollLeft =
    isRtlLanguage && containerScrollLeft
      ? Math.abs(containerScrollLeft)
      : containerScrollLeft
  const navigationChildren = responsiveNavigation ? children : undefined

  useEffect(() => {
    const currentContainer = scrollWrapperRef.current
    const dimensions = responsiveNavigation
      ? {
          scrollLeft: Math.abs(currentContainer?.scrollLeft ?? 0),
          clientWidth: currentContainer?.clientWidth,
          scrollWidth: currentContainer?.scrollWidth
        }
      : {scrollLeft, clientWidth, scrollWidth}
    const isHideNavButtons = hideNavButtonsOnlyForMobileSm
      ? isMobileSm
      : isMobile

    if (isHideNavButtons) {
      setNavButtonVisibility({
        next: false,
        prev: false
      })
      return
    }

    if (
      !isNil(dimensions.scrollLeft) &&
      !isNil(dimensions.clientWidth) &&
      !isNil(dimensions.scrollWidth)
    ) {
      const thresholdFromSides = 15

      const nextButtonVisibility =
        dimensions.scrollLeft + dimensions.clientWidth <
        dimensions.scrollWidth - thresholdFromSides
      const prevButtonVisibility = dimensions.scrollLeft > thresholdFromSides

      setNavButtonVisibility({
        next: nextButtonVisibility,
        prev: prevButtonVisibility
      })
    }
  }, [
    scrollPosition,
    viewportWidth,
    scrollWrapperRef,
    isMobile,
    isRtlLanguage,
    isMobileSm,
    hideNavButtonsOnlyForMobileSm,
    container,
    scrollLeft,
    clientWidth,
    scrollWidth,
    responsiveNavigation,
    navigationChildren
  ])

  useEffect(() => {
    const container = scrollWrapperRef.current
    if (!container) {
      return
    }

    const {firstElementChild} = container
    if (!firstElementChild) {
      return
    }

    if (typeof ResizeObserver === 'undefined') {
      // Measure once so the nav buttons still have a card width to scroll by.
      const {width} = firstElementChild.getBoundingClientRect()
      setCardWidth(width)
      if (width > 0) {
        setCardsPerClick(
          Math.max(1, Math.floor(container.clientWidth / width) - 1)
        )
      }

      return
    }

    const resizeObserver = new ResizeObserver(entries => {
      if (!responsiveNavigation) {
        for (const entry of entries) {
          const {width} = entry.target.getBoundingClientRect()
          setCardWidth(width)
          setCardsPerClick(Math.floor(container.clientWidth / width) - 1)
        }
        return
      }

      const {width} = firstElementChild.getBoundingClientRect()
      setCardWidth(width)
      setViewportWidth(container.clientWidth)
      if (width > 0) {
        setCardsPerClick(
          Math.max(1, Math.floor(container.clientWidth / width) - 1)
        )
      }
    })

    resizeObserver.observe(firstElementChild)
    if (responsiveNavigation) resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
    }
  }, [children, responsiveNavigation])

  const handleNavigateButtonClick = (direction: 'next' | 'prev') => () => {
    const container = scrollWrapperRef.current

    if (!container) {
      return
    }

    const scrollPerClick = cardsPerClick * cardWidth
    const scrollOffset = direction === 'next' ? -scrollPerClick : scrollPerClick
    const widthToScroll = isRtlLanguage
      ? container.scrollLeft + scrollOffset
      : container.scrollLeft - scrollOffset

    container.scroll({
      left: widthToScroll,
      behavior: 'smooth'
    })
  }

  const navigation = (
    <Box display="flex" gap="s300" flex="0 0 auto">
      {(['prev', 'next'] as const).map(direction => (
        <Button
          key={direction}
          ariaLabel={navigationButtonLabels?.[direction]}
          variant="tertiary"
          isRounded
          size={navigationButtonSize}
          disabled={!navButtonVisibility[direction]}
          iconStart={
            <Icon
              name={direction === 'prev' ? 'ChevronLeft' : 'ChevronRight'}
              hasMirror
            />
          }
          onClick={handleNavigateButtonClick(direction)}
        />
      ))}
    </Box>
  )

  return (
    <>
      {renderHeader?.(navigation)}
      <CardsAndNavButtonsWrapper>
        {!renderHeader && navButtonVisibility?.prev && (
          <NavigateButtonWrapper className="--direction-prev">
            <Button
              variant="tertiary"
              isFloating
              isRounded
              size={navigationButtonSize}
              iconStart={<Icon name="ChevronLeft" size="sm" />}
              onClick={handleNavigateButtonClick('prev')}
            />
          </NavigateButtonWrapper>
        )}
        <ScrollWrapper
          itemsGap={itemsGap}
          sidePadding={sidePadding}
          ref={scrollWrapperRef}
          className={cx(hasSidePaddings && '--has-side-paddings')}
        >
          {children}
        </ScrollWrapper>
        {!renderHeader && navButtonVisibility?.prev && (
          <EdgeFadeOverlay
            className="--direction-prev"
            data-id="edgeFadePrev"
          />
        )}
        {!renderHeader && navButtonVisibility?.next && (
          <EdgeFadeOverlay
            className="--direction-next"
            data-id="edgeFadeNext"
          />
        )}
        {!renderHeader && navButtonVisibility?.next && (
          <NavigateButtonWrapper className="--direction-next">
            <Button
              variant="tertiary"
              isFloating
              isRounded
              size={navigationButtonSize}
              iconStart={<Icon name="ChevronRight" size="sm" />}
              onClick={handleNavigateButtonClick('next')}
            />
          </NavigateButtonWrapper>
        )}
      </CardsAndNavButtonsWrapper>
    </>
  )
}
